import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  useTheme,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { ExternalSubscriptionItem } from '../../types/account';
import { MetadataDetails } from '../../types/authorization';
import ChangePacDialog, {
  formatAccountSummary,
  formatSubscriptionDate,
} from './Fintoc/ChangePacDialog';
import PayYourLoan from '../../feature/payYourLoan';

export function PreviousSubscriptionRow({ sub, label }: {
  sub: ExternalSubscriptionItem;
  label: string;
}) {
  const theme = useTheme();
  return (
    <Box sx={{
      width: '100%', mt: 5, paddingInline: 1,
    }}
    >
      <Box sx={{
        width: '100%', backgroundColor: theme.palette.grey[50], borderRadius: 2, paddingBlock: 2, paddingInline: 3,
      }}
      >
        <Typography
          variant="caption"
          component="div"
          sx={{
            fontWeight: 700,
            color: 'text.secondary',
            mb: 0.75,
            marginTop: 1,
          }}
        >
          {label.toUpperCase()}
        </Typography>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap',
        }}
        >
          <Typography variant="body2">
            {formatAccountSummary(sub.metadataDetails as MetadataDetails, sub.paymentMethod)
            || label}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {formatSubscriptionDate(sub.lastChangedAt || sub.createdAt)}
            </Typography>
            <Chip label="Anterior" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 22 }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export function LastChangeInfo({ subscription }: { subscription: ExternalSubscriptionItem }) {
  if (!subscription.lastChangedAt) return null;
  return (
    <Box
      sx={{
        mt: 2,
        mx: 2,
        mb: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        paddingBlock: 1,
        paddingInline: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        backgroundColor: '#f9f9f9',
      }}
    >
      <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
      <Box>
        {subscription.daysUntilNextChange !== undefined && (
          <Typography component="div" variant="caption">
            Próximo cambio disponible en
            {' '}
            <Typography component="span" fontWeight="bold" color="secondary">
              {subscription.daysUntilNextChange}
              {' '}
              días
            </Typography>
          </Typography>
        )}
        <Typography component="div" variant="caption" color="text.secondary">
          Último cambio
          {' '}
          {formatSubscriptionDate(subscription.lastChangedAt)}
        </Typography>
      </Box>
    </Box>
  );
}

interface SubscriptionActionsProps {
  subscriptions: ExternalSubscriptionItem[];
  activeSubscription: ExternalSubscriptionItem | undefined;
  token: string;
  idNumber: string;
  paymentMethodProvider: 'fintoc' | 'etpay' | 'getnet';
  changeButtonLabel: string;
  onSwitchSuccess: () => Promise<void>;
  onNewSubscriptionSuccess: () => Promise<void>;
  onDeleteSubscription: (subscriptionId: string) => Promise<void>;
}

export function SubscriptionActions({
  subscriptions,
  activeSubscription,
  token,
  idNumber,
  paymentMethodProvider,
  changeButtonLabel,
  onSwitchSuccess,
  onNewSubscriptionSuccess,
  onDeleteSubscription,
}: SubscriptionActionsProps) {
  return (
    <Box sx={{
      display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 5, px: 2, justifyContent: { xs: 'center', md: 'space-around' }, width: '100%',
    }}
    >
      <ChangePacDialog
        button={(
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<AutorenewIcon />}
            fullWidth
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              width: { xs: '85vw', md: '225px', xl: '230px' },
            }}
          >
            {changeButtonLabel}
          </Button>
        )}
        subscriptions={subscriptions}
        primarySubscription={activeSubscription}
        token={token}
        idNumber={idNumber}
        paymentMethodProvider={paymentMethodProvider}
        onSwitchSuccess={onSwitchSuccess}
        onNewSubscriptionSuccess={onNewSubscriptionSuccess}
        onDeleteSubscription={onDeleteSubscription}
      />
      <PayYourLoan token={token} pageCategory="PU - PAGOS" sxButton={{ width: { xs: '85vw', md: '225px', xl: '230px' } }} />
    </Box>
  );
}
